import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import type { RegisterLocationState } from './Register';
import { Card, Button, List, App, Modal, Form, Input, Spin, Alert, Select } from 'antd';
import { PlusOutlined, LogoutOutlined, BankOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getUsersByUid } from '../../api/users';
import { createWorkspace } from '../../api/workspaces';
import { createUserDocumentForWorkspace } from '../../api/auth';
import BrandLogo from '../../components/common/BrandLogo';
import type { User } from '../../types/users';
import type { CreateWorkspaceInput } from '../../types/workspaces';
import { COUNTRY_OPTIONS } from '../../constants/countries';
import i18n from '../../i18n';
import { postAuthWorkspacePath } from '../../utils/onboardingStep';

type CreateWorkspaceFormValues = {
  name: string;
  slug?: string;
  language: 'en' | 'es' | 'pt';
  currency: 'USD' | 'EUR' | 'BRL';
  presidentName?: string;
  presidentEmail?: string;
  presidentPhone?: string;
};

export const SelectWorkspace = () => {
  const { t } = useTranslation(['auth', 'common']);
  const { message } = App.useApp();
  const { firebaseUser, firestoreUser, setFirestoreUser, signOut, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const registerState = location.state as RegisterLocationState | undefined;
  const [profiles, setProfiles] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [form] = Form.useForm<CreateWorkspaceFormValues>();
  const autoOpenedCreateModalRef = useRef(false);

  useEffect(() => {
    if (!firebaseUser) return;
    let cancelled = false;
    getUsersByUid(firebaseUser.uid).then((list) => {
      if (!cancelled) {
        setProfiles(list);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [firebaseUser]);

  /** When user has no workspace memberships, open create-workspace once with registration data prefilled. */
  useEffect(() => {
    if (authLoading || loading || !firebaseUser) return;
    if (autoOpenedCreateModalRef.current) return;
    const hasWorkspace = profiles.some((p) => p.workspace?.id);
    if (hasWorkspace) return;

    autoOpenedCreateModalRef.current = true;
    const noWorkspaceProfile = profiles.find((p) => !p.workspace?.id);
    const pw = noWorkspaceProfile?.pendingWorkspace;
    const prefill = pw?.apartmentName ?? registerState?.apartmentName ?? '';
    const slug = prefill
      ? prefill.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').slice(0, 50)
      : '';

    form.setFieldsValue({
      name: prefill,
      slug,
      language: 'en',
      currency: 'USD',
      presidentName: firebaseUser.displayName || '',
      presidentEmail: firebaseUser.email || '',
      presidentPhone: '',
    });
    setCreateModalOpen(true);
  }, [authLoading, loading, firebaseUser, profiles, registerState?.apartmentName, form]);

  if (!firebaseUser) {
    return <Navigate to="/login" replace />;
  }

  if (firestoreUser?.workspace?.id) {
    return <Navigate to={postAuthWorkspacePath(firestoreUser.workspace)} replace />;
  }

  const withWorkspace = profiles.filter((p) => p.workspace?.id);
  const noWorkspaceProfile = profiles.find((p) => !p.workspace?.id);
  const pendingWorkspace = noWorkspaceProfile?.pendingWorkspace;
  const countryLabel =
    pendingWorkspace?.country != null
      ? COUNTRY_OPTIONS.find((c) => c.value === pendingWorkspace.country)?.label ?? pendingWorkspace.country
      : '';

  const openCreateWorkspaceModal = () => {
    const pw = noWorkspaceProfile?.pendingWorkspace;
    const prefill = pw?.apartmentName ?? registerState?.apartmentName ?? '';
    const slug = prefill
      ? prefill.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').slice(0, 50)
      : '';
    form.setFieldsValue({
      name: prefill,
      slug,
      language: 'en',
      currency: 'USD',
      presidentName: firebaseUser?.displayName || '',
      presidentEmail: firebaseUser?.email || '',
      presidentPhone: '',
    });
    setCreateModalOpen(true);
  };

  const handleSelectProfile = async (profile: User) => {
    if (!profile.workspace?.id) return;
    try {
      setSelecting(true);
      await setFirestoreUser(profile);
      navigate(postAuthWorkspacePath(profile.workspace), { replace: true });
    } catch {
      message.error(t('auth:selectWorkspaceError'));
    } finally {
      setSelecting(false);
    }
  };

  const handleCreateWorkspace = async (values: CreateWorkspaceFormValues) => {
    if (!firebaseUser) return;
    const name = values.name.trim();
    const slug =
      (values.slug || name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')).slice(0, 50) ||
      'workspace';
    try {
      setCreateLoading(true);
      // Firestore rejects `undefined` field values — only include optional president* when non-empty.
      const payload: CreateWorkspaceInput = {
        name,
        slug,
        customer_id: `web-${firebaseUser.uid}`,
        ownerUid: firebaseUser.uid,
        memberUids: [firebaseUser.uid],
        language: values.language,
        currency: values.currency,
        onboardingStep: 0,
      };
      const pn = values.presidentName?.trim();
      const pe = values.presidentEmail?.trim();
      const pp = values.presidentPhone?.trim();
      if (pn) payload.presidentName = pn;
      if (pe) payload.presidentEmail = pe;
      if (pp) payload.presidentPhone = pp;

      const workspace = await createWorkspace(payload);
      await i18n.changeLanguage(values.language);
      const workspaceRef = {
        id: workspace.id,
        name: workspace.name,
        slug: workspace.slug,
        language: workspace.language,
      };
      await createUserDocumentForWorkspace(
        firebaseUser.uid,
        firebaseUser.email || '',
        firebaseUser.displayName || name,
        workspaceRef,
        'administrator'
      );
      const profilesUpdated = await getUsersByUid(firebaseUser.uid);
      const newProfile = profilesUpdated.find((p) => p.workspace?.id === workspace.id);
      if (newProfile) {
        await setFirestoreUser(newProfile);
        setCreateModalOpen(false);
        form.resetFields();
        navigate(postAuthWorkspacePath(newProfile.workspace), { replace: true });
      } else {
        message.success(t('auth:workspaceCreated'));
        setProfiles(profilesUpdated);
        setCreateModalOpen(false);
        form.resetFields();
      }
    } catch (e) {
      console.error(e);
      message.error(t('auth:createWorkspaceError'));
    } finally {
      setCreateLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5' }}>
        <Spin size="large" tip={t('auth:loadingSession')} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: 24 }}>
      <div style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ marginBottom: 24 }}>
          <BrandLogo variant="bright" showTagline={true} />
        </div>
        <h2 style={{ marginBottom: 8, fontWeight: 600 }}>{t('auth:selectWorkspaceTitle')}</h2>
        <p style={{ color: '#666', marginBottom: 24 }}>{t('auth:selectWorkspaceSubtitle')}</p>

        {withWorkspace.length > 0 && (
          <List
            dataSource={withWorkspace}
            renderItem={(item) => (
              <List.Item>
                <Card
                  style={{ width: '100%', cursor: selecting ? 'wait' : 'pointer', textAlign: 'left' }}
                  onClick={() => handleSelectProfile(item)}
                  loading={selecting}
                >
                  <Card.Meta
                    avatar={<BankOutlined style={{ fontSize: 24 }} />}
                    title={item.workspace?.name ?? t('auth:unknownWorkspace')}
                    description={item.roles?.join(', ') || ''}
                  />
                </Card>
              </List.Item>
            )}
          />
        )}

        {withWorkspace.length === 0 && (
          <p style={{ color: '#666', marginBottom: 24 }}>{t('auth:noWorkspacesYet')}</p>
        )}

        <Button type="primary" icon={<PlusOutlined />} size="large" onClick={openCreateWorkspaceModal} style={{ marginBottom: 24 }}>
          {t('auth:createNewWorkspace')}
        </Button>

        <div>
          <Button icon={<LogoutOutlined />} onClick={() => signOut()}>
            {t('auth:logout')}
          </Button>
        </div>
      </div>

      <Modal
        title={t('auth:createWorkspaceTitle')}
        open={createModalOpen}
        onCancel={() => !createLoading && setCreateModalOpen(false)}
        footer={null}
        destroyOnClose={false}
      >
        {(pendingWorkspace?.apartmentName || pendingWorkspace?.country || pendingWorkspace?.region) && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('auth:workspaceFromRegistration')}
            description={
              [
                pendingWorkspace?.apartmentName,
                countryLabel || undefined,
                pendingWorkspace?.region,
              ]
                .filter(Boolean)
                .join(' · ') || undefined
            }
          />
        )}
        <Form form={form} layout="vertical" onFinish={handleCreateWorkspace}>
          <Form.Item name="name" label={t('auth:workspaceName')} rules={[{ required: true, message: t('auth:workspaceNameRequired') }]}>
            <Input placeholder={t('auth:workspaceNamePlaceholder')} />
          </Form.Item>
          <Form.Item
            name="language"
            label={t('auth:workspaceLanguage')}
            rules={[{ required: true, message: t('auth:workspaceLanguageRequired') }]}
          >
            <Select
              options={[
                { value: 'en', label: t('auth:language.en') },
                { value: 'es', label: t('auth:language.es') },
                { value: 'pt', label: t('auth:language.pt') },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="currency"
            label={t('auth:workspaceCurrency')}
            rules={[{ required: true, message: t('auth:workspaceCurrencyRequired') }]}
          >
            <Select
              options={[
                { value: 'USD', label: 'USD' },
                { value: 'EUR', label: 'EUR' },
                { value: 'BRL', label: 'BRL' },
              ]}
            />
          </Form.Item>
          <Form.Item name="slug" label={t('auth:workspaceSlug')}>
            <Input placeholder={t('auth:workspaceSlugPlaceholder')} />
          </Form.Item>
          <Form.Item name="presidentName" label={t('auth:workspacePresidentName')}>
            <Input placeholder={t('auth:fullNamePlaceholder')} />
          </Form.Item>
          <Form.Item
            name="presidentEmail"
            label={t('auth:workspacePresidentEmail')}
            rules={[{ type: 'email', message: t('auth:emailInvalid') }]}
          >
            <Input placeholder={t('auth:emailPlaceholder')} />
          </Form.Item>
          <Form.Item name="presidentPhone" label={t('auth:workspacePresidentPhone')}>
            <Input placeholder="+1 …" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={createLoading} block>
              {t('auth:createWorkspace')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
